import { CloseOutlined } from '@ant-design/icons'
import { Button, Tag, Typography } from 'antd'
import React, { FunctionComponent } from 'react'

import { FINANCING_SETTINGS } from '../../utils/buybackConst'
import { Fade } from '../Fade/Fade'
import { FinancingHelpSection } from '../FinancingHelpSection/FinancingHelpSection'
import { Money } from '../Money/Money'

export interface BuybackFormFinancingHelpProps {
  show: boolean
  onClose: () => void
}

const { Text } = Typography

export const BuybackFormFinancingHelp: FunctionComponent<BuybackFormFinancingHelpProps> =
  props => {
    return (
      <Fade show={props.show}>
        <div className="border border-gray-200 rounded p-4 mb-4 financing-help">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold m-0">
              Nápověda k hodnotám v tabulce a grafu
            </h3>
            <Button
              type="link"
              size="small"
              icon={<CloseOutlined />}
              onClick={props.onClose}
            >
              Skrýt nápovědu
            </Button>
          </div>

          <div className="space-y-3">
            <FinancingHelpSection highlightId="hl-mesic" title="Měsíc:">
              <div>
                Pořadové číslo měsíce od počátku nájemního vztahu/výkupu
                nemovitosti. Měsíce zvýrazněné modrým ohraničením{' ( '}
                <code className="font-bold border-l-2 border-primary px-1">
                  12
                </code>
                <code className="font-bold border-l-2 border-primary px-1">
                  18
                </code>
                <code className="font-bold border-l-2 border-primary px-1">
                  24
                </code>
                {'... ) '}
                představují tzv. referenční (milníkové) měsíce. Jsou to body, ke
                kterým se stanovuje cenová hladina pro případ předčasného
                zpětného odkupu.
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-vykupni-cena"
              title="Výkupní cena:"
              divider="Nemovitost"
            >
              <div>
                Celková finanční suma vynaložena při výkupu nemovitosti a
                zároveň výše investičního úvěru.
              </div>
              <div className="font-mono">
                Výpočet = <Text code>Tržní cena nemovitosti</Text>
                {' × '}
                <Text code>% nabídka výkupu nemovitosti</Text>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              title="Tržní hodnota:"
              highlightId="hl-trzni-hodnota"
            >
              <div>
                Odhadovaná hodnota nemovitosti na realitním trhu v konkrétním
                měsíci.
              </div>
              <div className="font-mono">
                Výpočet = <Text code>Tržní cena nemovitosti</Text>
                {' × ('}
                <Text code>% roční růst ceny nemovitosti</Text>
                {' ÷ 12) ^ '}
                <code className="text-primary hl-mesic">Měsíc</code>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-uroky-aktualni"
              title="Aktuální:"
              divider="Úroky investora"
            >
              <div>
                Vyčíslení peněžní hodnoty úroku investičního úvěru v konkrétním
                měsíci.
              </div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-vykupni-cena">
                  Výkupní cena
                </code>
                {' × ('}
                <Text code>Úrok investora (p.a.)</Text>
                {' ÷ 12) ^ '}
                <code className="text-primary hl-mesic">Měsíc</code>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-uroky-pri-prodeji"
              title="Při prodeji:"
            >
              <div>
                Vyčíslení peněžní hodnoty úroku investičního úvěru v měsíci, v
                němž je očekávána realizace prodeje nemovitosti na realitním
                trhu.
              </div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-vykupni-cena">
                  Výkupní cena
                </code>
                {' × ('}
                <Text code>Úrok investora (p.a.)</Text>
                {' ÷ 12) ^ ('}
                <code className="text-primary hl-mesic">Měsíc</code>
                {' + '}
                <Text code>Doba potřebná k tržnímu prodeji (měs.)</Text>
                {')'}
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-ke-splaceni-aktualni"
              title="Aktuální:"
              divider="Ke splacení investorovi"
            >
              <div>
                Celková částka, kterou je třeba uhradit investorovi v konkrétním
                měsíci, pokud by došlo ke zpětnému odkupu.
              </div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-vykupni-cena">
                  Výkupní cena
                </code>
                {' + '}
                <code className="text-primary hl-uroky-aktualni">
                  Úroky investora aktuální
                </code>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-ke-splaceni-pri-prodeji"
              title="Při prodeji:"
            >
              <div>
                Celková částka, kterou je třeba uhradit investorovi k měsíci,
                kdy je očekávána realizace prodeje nemovitosti na realitním
                trhu.
              </div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-vykupni-cena">
                  Výkupní cena
                </code>
                {' + '}
                <code className="text-primary hl-uroky-pri-prodeji">
                  Úroky investora při prodeji
                </code>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-inkas-najemne"
              title="Nájemné:"
              divider="Inkasovaný příjem"
            >
              <div>
                Celková částka inkasovaná z pronájmu nemovitosti (pouze čisté
                nájemné bez služeb a nákladů) do konkrétního měsíce.
              </div>
              <div className="font-mono">
                Výpočet = <Text code>Měsíční nájemné</Text>
                {' × '}
                <code className="text-primary hl-mesic">Měsíc</code>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-inkas-pri-prodeji"
              title="Tržní prodej:"
            >
              <div>
                Částka inkasovaná v případě prodeje nemovitosti na realitním
                trhu po odečtení nákladů spojených s prodejem. (vybrané nájemné
                není započítáno)
              </div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-trzni-hodnota">
                  Tržní hodnota
                </code>
                {' - ('}
                <code className="text-primary hl-trzni-hodnota">
                  Tržní hodnota
                </code>
                {' × '}
                <Text code>Náklady tržního prodeje</Text>
                {')'}
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              highlightId="hl-inkas-zpetny-odkup"
              title="Zpětný odkup:"
            >
              <div>
                Částka inkasovaná v případě zpětného odkupu nemovitosti. Vychází
                z cenové hladiny platné k příslušnému referenčnímu (milníkovému)
                měsíci. Milníkem je v prvním roce měsíc číslo 12 a poté každý
                následující 6. měsíc (18, 24, 30, 36, ...). Pokud sjednaná délka
                nájmu nekončí přesně na milníku, považuje se poslední měsíc
                nájmu za další milník. (vybrané nájemné není započítáno)
              </div>
              <div className="font-mono">
                Výpočet = <Text code>Cena zpětného odkupu</Text>
              </div>
            </FinancingHelpSection>

            <FinancingHelpSection
              divider="Bilance zisků a ztrát"
              title="Tržní prodej:"
            >
              <div>
                Celková bilance v případě prodeje nemovitosti na realitním trhu.
              </div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-inkas-pri-prodeji">
                  Inkas tržní prodej
                </code>
                {'  +  '}
                <code className="text-primary hl-inkas-najemne">
                  Inkas nájemné
                </code>
                {'  -  '}
                <code className="text-primary hl-ke-splaceni-pri-prodeji">
                  Ke splacení investorovi při prodeji
                </code>
              </div>
            </FinancingHelpSection>
            <FinancingHelpSection title="Zpětný odkup:">
              <div>Celková bilance v případě zpětného odkupu nemovitosti.</div>
              <div className="font-mono">
                Výpočet ={' '}
                <code className="text-primary hl-inkas-zpetny-odkup">
                  Inkas zpětný odkup
                </code>
                {'  +  '}
                <code className="text-primary hl-inkas-najemne">
                  Inkas nájemné
                </code>
                {'  -  '}
                <code className="text-primary hl-ke-splaceni-aktualni">
                  Ke splacení investorovi aktuální
                </code>
              </div>
            </FinancingHelpSection>
            <FinancingHelpSection divider="Dostupnost financování">
              Kontrolují se hodnoty{' '}
              <code className="text-primary hl-bilance-trzni-prodej">
                Bilance tržního prodeje
              </code>
              {' i '}
              <code className="text-primary hl-bilance-zpetny-odkup">
                Bilance zpětného odkupu
              </code>{' '}
              pro všechny měsíce po dobu smluvního vztahu.
            </FinancingHelpSection>
            <FinancingHelpSection
              title={
                <Tag color="success" className="!text-lime-600 font-normal">
                  Předschváleno
                </Tag>
              }
            >
              Všechny bilance dosahují hodnot vyšších než{' '}
              <Money
                amount={FINANCING_SETTINGS.balanceWarningLevel}
                symbol="Kč"
                decimalNumbers={0}
              />
            </FinancingHelpSection>
            <FinancingHelpSection
              title={
                <Tag color="warning" className="font-normal">
                  Ke schválení
                </Tag>
              }
            >
              Všechny bilance jsou kladné, avšak minimálně jedna pod hranicí{' '}
              <Money
                amount={FINANCING_SETTINGS.balanceWarningLevel}
                symbol="Kč"
                decimalNumbers={0}
              />
            </FinancingHelpSection>

            <FinancingHelpSection
              title={
                <Tag color="error" className="font-normal">
                  Zamítnuto
                </Tag>
              }
            >
              Minimálně jedna z bilancí nabývá záporných hodnot.
            </FinancingHelpSection>

            <FinancingHelpSection title="Tip">
              V tabulce se můžete do stran posouvat pomocí{' '}
              <Text keyboard>shift</Text> + <Text keyboard>scroll</Text>
            </FinancingHelpSection>
          </div>
        </div>
      </Fade>
    )
  }
